import React, { useState } from 'react';
import { Box, Divider, Stack } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import LinkIcon from '@mui/icons-material/Link';
import GroupsIcon from '@mui/icons-material/Groups';
import {
    OrderItem as IOrderItem,
    OrderStatuses as OrderStatusEnum,
    ResourceType,
    useCancelOrder,
    useSendOrder,
} from '@travelity/api';
import OutboundIcon from '@mui/icons-material/Outbound';
import { formatMoney } from '@travelity/utils';
import {
    Card,
    IconButton,
    IconButtonGroup,
    SquareCard,
    StatusTag,
    StatusTagStatuses,
} from '@travelity/ui';
import { useTranslation } from 'react-i18next';
import { CopyUrlModal } from '../copy-url-modal';

import { useToast } from '../../hooks';
import { OrderStatus } from '../order-status';
import { PaymentStatusTag } from '../payment-status';
import { OverlayWithReason } from '../overlay-with-reason';
import { ReasonModal } from '../booking-item/components/reason-modal';
import { useResourcePermissions, useUserContext } from '../../contexts/user';

export interface OrderItemProps {
    order: IOrderItem;
    refetch: () => void;
    isSelected?: boolean;
}

const OrderItem: React.FC<OrderItemProps> = ({
    order,
    refetch,
    isSelected,
}) => {
    // const { t } = useTranslation('order');
    const { financials } = order;
    const { showError, showSuccess } = useToast();
    const { t } = useTranslation();
    const publicCheckoutEnabled = true;

    const { user } = useUserContext();
    const { currency = '' } = user || {};

    const { mutate: send, isLoading: isSendingEmail } = useSendOrder({
        onSuccess: () => {
            showSuccess('ORDER_SENT_EMAIL');
        },
    });
    const [deletingOrder, setDeletingOrder] = useState<string>();
    const [urlModalOpen, setUrlModalOpen] = useState(false);
    const { mutate: removeOrder, isLoading: isRemoveOrderLoading } =
        useCancelOrder({
            onSuccess: () => {
                setDeletingOrder(undefined);
                refetch();
                showSuccess('CANCEL_ORDER_SUCCESS', {
                    orderNumber: order.number,
                    customerName: order.participants.customer.fullName,
                });
            },
            onError: () => {
                showError('CANCEL_ORDER_FAILURE', {
                    orderNumber: order.number,
                    customerName: order.participants.customer.fullName,
                });
            },
        });
    const discountFormatted =
        financials?.discount?.type === 'absolute'
            ? formatMoney(financials.discount?.amount, currency)
            : `${financials?.discount?.amount}%`;

    const finalPrice = financials
        ? formatMoney(financials?.total, currency)
        : '';
    const permissions = useResourcePermissions(ResourceType.ORDER);

    return (
        <>
            <Card
                isSelected={isSelected}
                sx={{
                    height: '190px',
                }}
                direction="row"
                justifyContent="space-evenly"
                buttons={
                    <>
                        {order.status !== OrderStatusEnum.CANCELLED ? (
                            <IconButtonGroup>
                                <IconButton
                                    icon={<OutboundIcon fontSize="small" />}
                                    href={`/order/${order.id}`}
                                    tooltip="Go to Order"
                                />
                                <Divider sx={{ mx: 0.75 }} />
                                <IconButton
                                    icon={<VisibilityIcon fontSize="small" />}
                                    href={`/orders/${order.id}`}
                                    tooltip="Preview"
                                />
                                {permissions.delete && (
                                    <>
                                        <Divider sx={{ mx: 0.75 }} />
                                        <IconButton
                                            icon={
                                                <DeleteIcon fontSize="small" />
                                            }
                                            hoverColor="error.main"
                                            onClick={() =>
                                                setDeletingOrder(order.id)
                                            }
                                            tooltip="Cancel"
                                        />
                                    </>
                                )}
                            </IconButtonGroup>
                        ) : (
                            <IconButtonGroup>
                                <IconButton
                                    icon={<VisibilityIcon fontSize="small" />}
                                    href={`/orders/${order.id}`}
                                    tooltip="Preview"
                                />
                            </IconButtonGroup>
                        )}
                        {publicCheckoutEnabled && (
                            <IconButtonGroup>
                                <IconButton
                                    icon={
                                        <ForwardToInboxIcon fontSize="small" />
                                    }
                                    disabled={isSendingEmail}
                                    tooltip="Send email"
                                    onClick={() => send({ orderId: order.id })}
                                />
                                <Divider sx={{ mx: 0.75 }} />
                                <IconButton
                                    icon={<LinkIcon fontSize="small" />}
                                    tooltip="Copy link"
                                    onClick={() => setUrlModalOpen(true)}
                                />
                            </IconButtonGroup>
                        )}
                    </>
                }
            >
                <Stack
                    sx={{
                        width: '50%',
                        filter:
                            order.status === OrderStatusEnum.CANCELLED
                                ? 'blur(2px)'
                                : undefined,
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            px: 2,
                            height: '46px',
                            py: '10px',
                            bgcolor: '#F4F6FA',
                            borderRadius: '12px 0px 0px 0px',
                        }}
                    >
                        <Box component="span" sx={{ color: '#8F97AA' }}>
                            Order #{order.number}
                        </Box>
                    </Stack>
                    <Stack
                        sx={{
                            grow: 2,
                            pl: 2,
                            pr: 2 + (3 - 1),
                            py: '10px',
                            height: '144px',
                        }}
                        direction="row"
                        justifyContent="space-evenly"
                        gap={1}
                    >
                        <SquareCard
                            title="Products"
                            value={`${order.productsCount}`}
                            Icon={ListAltIcon}
                        />
                        <SquareCard
                            title="Bookings"
                            value={`${order.bookingsCount}`}
                            Icon={CalendarMonthIcon}
                        />
                        <SquareCard
                            title="Registered"
                            value={`${order.travelersCount}`}
                            Icon={GroupsIcon}
                        />
                    </Stack>
                </Stack>
                <Stack
                    sx={{
                        width: '50%',
                        borderLeft: '2px solid #C9CEDC',
                        position: 'relative',
                        filter:
                            order.status === OrderStatusEnum.CANCELLED
                                ? 'blur(2px)'
                                : undefined,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="end"
                        gap={2}
                        sx={{
                            px: 2,
                            height: '46px',
                            py: '10px',
                            bgcolor: '#F4F6FA',
                            borderRadius: '0px 12px 0px 0px',
                        }}
                    >
                        {order.noActiveBookings ? (
                            <StatusTag
                                status={StatusTagStatuses.NEUTRAL}
                                value={t('NO ACTIVE BOOKINGS').toUpperCase()}
                            />
                        ) : (
                            <>
                                <OrderStatus status={order.status} />
                                {order.financials?.status && (
                                    <PaymentStatusTag
                                        status={order.financials?.status}
                                    />
                                )}
                            </>
                        )}
                    </Stack>
                    <Stack sx={{ height: '144px', overflow: 'hidden', p: 2 }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 1 }}
                        >
                            <Box
                                sx={{
                                    color: '#6B748C',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            >
                                Customer
                            </Box>
                            <Box
                                sx={{
                                    color: '#6B758D',
                                    textAlign: 'right',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                }}
                            >
                                {`${order.participants.customer.fullName}`}
                            </Box>
                        </Stack>
                        {financials && (
                            <>
                                {!!financials.subtotal && (
                                    <>
                                        <Divider sx={{ mb: 1 }} />
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            sx={{ mb: 1 }}
                                        >
                                            <Box
                                                sx={{
                                                    color: '#6B748C',
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                Total before discount
                                            </Box>
                                            <Box
                                                sx={{
                                                    color: '#6B758D',
                                                    textAlign: 'right',
                                                    fontSize: '14px',
                                                    fontWeight: '400',
                                                }}
                                            >
                                                {formatMoney(
                                                    financials.subtotal,
                                                    currency
                                                )}
                                            </Box>
                                        </Stack>
                                    </>
                                )}
                                {!!financials.discount?.amount && (
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        sx={{ mb: 1 }}
                                    >
                                        <Box
                                            sx={{
                                                color: '#6B748C',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                            }}
                                        >
                                            Discount
                                        </Box>
                                        <Box
                                            sx={{
                                                color: '#6B758D',
                                                textAlign: 'right',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                            }}
                                        >
                                            {discountFormatted}
                                        </Box>
                                    </Stack>
                                )}
                                <Divider />
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    sx={{ mt: 1, mb: 1.5 }}
                                >
                                    <Box
                                        sx={{
                                            color: '#6B748C',
                                            fontSize: '15px',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {financials.discount?.amount
                                            ? 'Total after discount'
                                            : 'Total'}
                                    </Box>
                                    <Box
                                        sx={{
                                            color: '#2B395B',
                                            textAlign: 'right',
                                            fontSize: '15px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        {finalPrice}
                                    </Box>
                                </Stack>
                            </>
                        )}
                    </Stack>
                </Stack>
                {order.status === OrderStatusEnum.CANCELLED && (
                    <OverlayWithReason
                        title="Cancelled"
                        reason={order.reason}
                    />
                )}
            </Card>
            <ReasonModal
                title="Are you sure you want to cancel this order?"
                content="Provide the reason for cancelling this order"
                confirmText="Confirm"
                open={!!deletingOrder}
                isLoading={isRemoveOrderLoading}
                handleCancel={() => setDeletingOrder(undefined)}
                handleConfirm={reason => {
                    removeOrder({
                        orderId: deletingOrder as string,
                        reason,
                    });
                }}
            />
            <CopyUrlModal
                open={urlModalOpen}
                onClose={() => setUrlModalOpen(false)}
                orderId={order.id}
            />
        </>
    );
};

export default OrderItem;
